/*----------------------------------
- GENERATED FILE
----------------------------------*/

// This file is generated by Proteum from client/pages/marketing/agencies.tsx.
// Do not edit it manually.

import type __GeneratedRouteApp from "@/client/index";

/*----------------------------------
- DEPENDANCES
----------------------------------*/

import React from 'react';
import { ArrowRight, BarChart3, CheckCircle, DollarSign, Eye, FileCheck, Globe, Lock, MessageSquare, Settings, Tag, Target, Users, Wrench, X as XIcon } from 'lucide-preact';

/*----------------------------------
- APP
----------------------------------*/

import PublicHeader from '@/client/components/marketing/PublicHeader';
import Page from '@/client/pages/_layout/_page';

/*----------------------------------
- CONTROLEUR
----------------------------------*/

export const __register = (app: __GeneratedRouteApp) => {
  const { Router } = app;
  return Router.page('/agencies', { ...({
  static: {
    refresh: '1h',
    urls: ['/agencies']
  }
}), ...{ filepath: "/app/client/pages/marketing/agencies.tsx", sourceLocation: { line: 20, column: 1 }, id: "marketing_agencies" } }, null, ({}) => {
  const [calendlyOpen, setCalendlyOpen] = React.useState(false);
  const [selectedModel, setSelectedModel] = React.useState<'ondemand' | 'whitelabel'>('ondemand');
  const fadeIn = {
    hidden: {
      opacity: 0,
      y: 20
    },
    visible: {
      opacity: 1,
      y: 0
    }
  };
  const scrollToOptions = () => {
    document.getElementById('options')?.scrollIntoView({
      behavior: 'smooth'
    });
  };
  return <Page withLayout={false}>
            <div className="min-h-screen bg-white">
                <PublicHeader currentPage="partners" />

                <section className="relative pt-32 pb-20 px-6 lg:px-8 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 overflow-hidden">
                    <div className="absolute inset-0 opacity-10">
                        <div className="absolute inset-0" style={{
            backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)',
            backgroundSize: '40px 40px'
          }} />
                    </div>

                    <div className="max-w-7xl mx-auto relative z-10">
                        <div className="grid lg:grid-cols-2 gap-12 items-center">
                            <div>
                                <div className="inline-block mb-6">
                                    <span className="bg-orange-500/20 text-orange-300 px-4 py-2 rounded-full text-sm font-semibold border border-orange-500/30">
                                        For Recruitment Agencies
                                    </span>
                                </div>

                                <h1 className="text-4xl lg:text-5xl font-bold text-white mb-6 leading-tight">
                                    Your network has limits. CrossPath doesn't.
                                </h1>

                                <p className="text-xl text-slate-300 mb-8 leading-relaxed">
                                    Access 10,000+ vetted recruiters across 42+ countries — on your terms. Whether you need occasional overflow coverage or a full white-label infrastructure for your team, CrossPath has a model that fits.
                                </p>

                                <div className="flex flex-col sm:flex-row gap-4">
                                    <button type="button" onClick={() => setCalendlyOpen(true)} className="bg-orange-500 hover:bg-orange-600 text-white px-8 py-4 rounded-lg font-semibold text-lg transition-all shadow-lg hover:shadow-xl inline-flex items-center justify-center gap-2">
                                        Book a Consultancy Call
                                        <ArrowRight size={20} />
                                    </button>
                                    <button type="button" onClick={scrollToOptions} className="border-2 border-slate-600 text-white hover:bg-slate-800 px-8 py-4 rounded-lg font-semibold text-lg transition-all inline-flex items-center justify-center gap-2">
                                        Compare Both Models
                                        <ArrowRight size={20} />
                                    </button>
                                </div>
                            </div>

                            <div className="relative hidden lg:block">
                                <div className="relative w-full h-96">
                                    <div className="absolute inset-0 bg-gradient-to-br from-orange-500/20 to-blue-500/20 rounded-2xl blur-3xl" />
                                    <div className="relative bg-slate-800/50 backdrop-blur-sm border border-slate-700 rounded-2xl p-8 h-full flex items-center justify-center">
                                        <div className="relative w-full h-full">
                                            <div className="absolute inset-0 flex items-center justify-center">
                                                <div className="w-16 h-16 bg-orange-500 rounded-full flex items-center justify-center">
                                                    <Target className="w-8 h-8 text-white" />
                                                </div>
                                            </div>
                                            {[...Array(12)].map((_, index) => <div key={index} className="absolute w-10 h-10 bg-blue-500/30 rounded-full border border-blue-500/50 flex items-center justify-center" style={{
                      left: `${50 + 40 * Math.cos(index * Math.PI * 2 / 12)}%`,
                      top: `${50 + 40 * Math.sin(index * Math.PI * 2 / 12)}%`,
                      transform: 'translate(-50%, -50%)'
                    }}>
                                                    <Users className="w-5 h-5 text-blue-400" />
                                                </div>)}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                <section className="py-20 px-6 lg:px-8 bg-slate-50">
                    <div className="max-w-7xl mx-auto">
                        <div className="text-center mb-16">
                            <h2 className="text-4xl font-bold text-slate-900 mb-4">
                                The limits every agency hits
                            </h2>
                        </div>

                        <div className="grid md:grid-cols-3 gap-8">
                            {[{
              icon: Globe,
              title: 'Geographic blind spots',
              text: "Your client needs a profile in a market you don't cover. You either say no — or scramble. Both options cost you the relationship."
            }, {
              icon: Users,
              title: 'Capacity ceilings',
              text: "Your consultants are stretched. A new mandate arrives and you can't staff it properly. Quality drops. Deadlines slip. Clients notice."
            }, {
              icon: Settings,
              title: 'Fragmented tooling',
              text: 'Candidate tracking, client communication, recruiter management — all in different tools. No unified view. No scalable process.'
            }].map((item, index) => <div key={item.title} className="bg-white rounded-xl p-8 border-2 border-slate-200 hover:border-orange-300 hover:shadow-lg transition-all">
                                    <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center mb-4">
                                        <item.icon className="w-6 h-6 text-orange-600" />
                                    </div>
                                    <h3 className="text-xl font-bold text-slate-900 mb-3">{item.title}</h3>
                                    <p className="text-slate-600 leading-relaxed">{item.text}</p>
                                </div>)}
                        </div>
                    </div>
                </section>

                <section id="options" className="py-20 px-6 lg:px-8 bg-white">
                    <div className="max-w-7xl mx-auto">
                        <div className="text-center mb-12">
                            <h2 className="text-4xl font-bold text-slate-900 mb-4">
                                Two ways to work with CrossPath
                            </h2>
                            <p className="text-xl text-slate-600 mb-8">
                                One for occasional needs. One for agencies ready to scale.
                            </p>

                            <div className="inline-flex items-center bg-slate-100 rounded-full p-2 gap-2">
                                <button type="button" onClick={() => setSelectedModel('ondemand')} className={`px-8 py-3 rounded-full font-semibold transition-all ${selectedModel === 'ondemand' ? 'bg-orange-500 text-white shadow-lg' : 'text-slate-600 hover:text-slate-900'}`}>
                                    On-Demand Access
                                </button>
                                <button type="button" onClick={() => setSelectedModel('whitelabel')} className={`px-8 py-3 rounded-full font-semibold transition-all ${selectedModel === 'whitelabel' ? 'bg-blue-500 text-white shadow-lg' : 'text-slate-600 hover:text-slate-900'}`}>
                                    White-Label Infrastructure
                                </button>
                            </div>
                        </div>

                        {selectedModel === 'ondemand' && <div className="bg-white rounded-2xl p-8 border-2 border-orange-300 shadow-xl">
                                <div className="text-center mb-8">
                                    <h3 className="text-3xl font-bold text-slate-900 mb-3">
                                        Need extra coverage on a specific mandate? We handle it.
                                    </h3>
                                    <p className="text-xl text-slate-600">
                                        No platform access needed. You brief us — we deliver.
                                    </p>
                                </div>

                                <div className="grid lg:grid-cols-2 gap-12 mb-8">
                                    <div>
                                        <h4 className="text-2xl font-bold text-slate-900 mb-6">How it works</h4>
                                        <div className="space-y-6">
                                            {[{
                    num: '1',
                    title: 'You brief your CrossPath CSM',
                    text: 'You contact your dedicated CSM directly — by call or email. You share the mandate details, your requirements, and your constraints. No platform login required.'
                  }, {
                    num: '2',
                    title: 'You set the parameters',
                    text: 'You define mission visibility (specific recruiters or broader network), the payout for recruiters, and any specific criteria. You stay in control of your sourcing strategy.'
                  }, {
                    num: '3',
                    title: 'CrossPath manages the execution',
                    text: 'Your CSM qualifies the brief, activates the right recruiters, and validates candidates before delivery. You receive pre-screened profiles — ready for your client.'
                  }, {
                    num: '4',
                    title: 'You place. You pay on success.',
                    text: 'Commercial terms are confirmed before activation. You pay the recruiter directly — CrossPath is not involved in that transaction.'
                  }].map(step => <div key={step.num} className="flex gap-4">
                                                    <div className="flex-shrink-0 w-10 h-10 bg-orange-500 rounded-lg flex items-center justify-center font-bold text-white">
                                                        {step.num}
                                                    </div>
                                                    <div>
                                                        <h5 className="font-bold text-slate-900 mb-2">{step.title}</h5>
                                                        <p className="text-slate-600 text-sm leading-relaxed">{step.text}</p>
                                                    </div>
                                                </div>)}
                                        </div>
                                    </div>

                                    <div>
                                        <h4 className="text-2xl font-bold text-slate-900 mb-6">What you control</h4>
                                        <div className="space-y-6">
                                            {[{
                    icon: Target,
                    title: 'Mission visibility',
                    text: 'Define which recruiters can see and apply to your mandate'
                  }, {
                    icon: DollarSign,
                    title: 'Recruiter payout',
                    text: 'You set the compensation terms for recruiters on your mission'
                  }, {
                    icon: Eye,
                    title: 'Client relationship',
                    text: 'Your client never knows CrossPath is involved'
                  }, {
                    icon: FileCheck,
                    title: 'Candidate review',
                    text: 'You receive profiles with recruiter notes, ready to present to your client'
                  }].map(item => <div key={item.title} className="flex gap-4">
                                                    <div className="flex-shrink-0 w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
                                                        <item.icon className="w-5 h-5 text-orange-600" />
                                                    </div>
                                                    <div>
                                                        <h5 className="font-bold text-slate-900 mb-1">{item.title}</h5>
                                                        <p className="text-slate-600 text-sm leading-relaxed">{item.text}</p>
                                                    </div>
                                                </div>)}
                                        </div>
                                    </div>
                                </div>

                                <div className="bg-slate-50 rounded-lg p-6 text-center">
                                    <div className="flex flex-col md:flex-row items-center justify-center gap-6 text-sm text-slate-600">
                                        <div className="flex items-center gap-2">
                                            <CheckCircle className="w-4 h-4 text-green-500" />
                                            <span>Commercial terms confirmed before activation</span>
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <CheckCircle className="w-4 h-4 text-green-500" />
                                            <span>You pay the recruiter directly — CrossPath does not invoice on their behalf in this model</span>
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <CheckCircle className="w-4 h-4 text-green-500" />
                                            <span>No minimum volume commitment.</span>
                                        </div>
                                    </div>
                                </div>
                            </div>}

                        {selectedModel === 'whitelabel' && <div className="bg-white rounded-2xl p-8 border-2 border-blue-300 shadow-xl">
                                <div className="text-center mb-8">
                                    <h3 className="text-3xl font-bold text-slate-900 mb-3">
                                        Give your agency a global operating system. Keep full control.
                                    </h3>
                                    <p className="text-xl text-slate-600">
                                        Your recruiters, your missions, your process — powered by CrossPath, invisible to your clients.
                                    </p>
                                </div>

                                <div className="grid lg:grid-cols-2 gap-12 mb-8">
                                    <div>
                                        <h4 className="text-2xl font-bold text-slate-900 mb-6">What your agency gets</h4>
                                        <div className="space-y-6">
                                            {[{
                    icon: Wrench,
                    title: 'Full platform access for your team',
                    text: "Your recruiters log in to CrossPath under your agency's environment. ATS, mission management, candidate tracking, performance dashboard — all in one place."
                  }, {
                    icon: Target,
                    title: 'You act as CSM',
                    text: 'You manage the entire recruitment process directly. No intermediary. You qualify mandates, activate recruiters, and control the pipeline yourself.'
                  }, {
                    icon: MessageSquare,
                    title: 'Active recruiter outreach',
                    text: 'You can promote any mission to the entire CrossPath recruiter network and directly invite specific recruiters to apply.'
                  }, {
                    icon: BarChart3,
                    title: 'Full pipeline visibility',
                    text: 'Track every recruiter, every candidate, every mandate stage in real time. Complete operational control.'
                  }, {
                    icon: Tag,
                    title: 'Your brand, always',
                    text: 'CrossPath is invisible to your clients. They see only your agency at every step.'
                  }].map(item => <div key={item.title} className="flex gap-4">
                                                    <div className="flex-shrink-0 w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                                                        <item.icon className="w-5 h-5 text-blue-600" />
                                                    </div>
                                                    <div>
                                                        <h5 className="font-bold text-slate-900 mb-1">{item.title}</h5>
                                                        <p className="text-slate-600 text-sm leading-relaxed">{item.text}</p>
                                                    </div>
                                                </div>)}
                                        </div>
                                    </div>

                                    <div className="space-y-8">
                                        <div>
                                            <h4 className="text-2xl font-bold text-slate-900 mb-6">You decide who sees each mandate</h4>
                                            <div className="space-y-4">
                                                <div className="bg-slate-50 rounded-lg p-4 border border-slate-200">
                                                    <div className="flex items-center gap-3 mb-2">
                                                        <Lock className="w-5 h-5 text-slate-600" />
                                                        <h5 className="font-bold text-slate-900">Internal only</h5>
                                                    </div>
                                                    <p className="text-slate-600 text-sm">Visible exclusively to your agency's recruiters on the platform</p>
                                                </div>
                                                <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
                                                    <div className="flex items-center gap-3 mb-2">
                                                        <Globe className="w-5 h-5 text-blue-600" />
                                                        <h5 className="font-bold text-slate-900">Open to CrossPath network</h5>
                                                    </div>
                                                    <p className="text-slate-600 text-sm">Promoted to all 10,000+ recruiters, with active invite capability</p>
                                                </div>
                                                <div className="bg-orange-50 rounded-lg p-4 border border-orange-200">
                                                    <div className="flex items-center gap-3 mb-2">
                                                        <Settings className="w-5 h-5 text-orange-600" />
                                                        <h5 className="font-bold text-slate-900">Per mandate</h5>
                                                    </div>
                                                    <p className="text-slate-600 text-sm">You choose visibility mandate by mandate, based on sensitivity and urgency</p>
                                                </div>
                                            </div>
                                        </div>

                                        <div>
                                            <h4 className="text-2xl font-bold text-slate-900 mb-6">Flexible operating flow</h4>
                                            <div className="grid md:grid-cols-2 gap-4 mb-4">
                                                <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg p-4 border border-blue-200">
                                                    <div className="inline-block bg-blue-500 text-white px-3 py-1 rounded-full text-xs font-bold mb-3">
                                                        Network recruiter
                                                    </div>
                                                    <p className="text-slate-700 text-sm leading-relaxed">
                                                        CrossPath coordinates the recruiter's share. You don't manage that transfer directly.
                                                    </p>
                                                </div>
                                                <div className="bg-gradient-to-br from-orange-50 to-orange-100 rounded-lg p-4 border border-orange-200">
                                                    <div className="inline-block bg-orange-500 text-white px-3 py-1 rounded-full text-xs font-bold mb-3">
                                                        Your team member
                                                    </div>
                                                    <p className="text-slate-700 text-sm leading-relaxed">
                                                        You pay your recruiter directly. CrossPath is not involved in that transaction.
                                                    </p>
                                                </div>
                                            </div>
                                            <p className="text-slate-600 text-sm text-center">
                                                In both cases, commercial terms are confirmed before the mandate is activated.
                                            </p>
                                        </div>
                                    </div>
                                </div>

                                <div className="bg-slate-50 rounded-lg p-8">
                                    <h4 className="text-2xl font-bold text-slate-900 mb-6 text-center">
                                        White-label setup — based on your team size
                                    </h4>
                                    <div className="grid md:grid-cols-3 gap-6">
                                        {[{
                  tier: 'Starter',
                  size: 'Up to 10 recruiters',
                  desc: 'For boutique agencies building their first unified infrastructure'
                }, {
                  tier: 'Growth',
                  size: 'Up to 30 recruiters',
                  desc: 'For mid-size agencies scaling across multiple markets'
                }, {
                  tier: 'Enterprise',
                  size: '30+ recruiters',
                  desc: 'Custom setup, dedicated support, advanced reporting'
                }].map(plan => <div key={plan.tier} className="bg-white rounded-lg p-6 border border-slate-200 hover:border-blue-300 hover:shadow-lg transition-all">
                                                <h5 className="text-xl font-bold text-slate-900 mb-2">{plan.tier}</h5>
                                                <p className="text-blue-600 font-semibold mb-3">{plan.size}</p>
                                                <p className="text-slate-600 text-sm leading-relaxed">{plan.desc}</p>
                                            </div>)}
                                    </div>
                                    <p className="text-center text-slate-600 text-sm mt-6">
                                        Commercial terms are confirmed during your onboarding call. Every agency setup is different.
                                    </p>
                                </div>
                            </div>}
                    </div>
                </section>

                <section className="py-20 px-6 lg:px-8 bg-slate-50">
                    <div className="max-w-7xl mx-auto">
                        <div className="text-center mb-12">
                            <h2 className="text-4xl font-bold text-slate-900 mb-4">
                                Not sure which model fits? Here's the breakdown.
                            </h2>
                        </div>

                        <div className="bg-white rounded-xl shadow-lg overflow-hidden">
                            <div className="overflow-x-auto">
                                <table className="w-full">
                                    <thead className="bg-slate-100">
                                        <tr>
                                            <th className="px-6 py-4 text-left text-sm font-bold text-slate-900" />
                                            <th className="px-6 py-4 text-center text-sm font-bold text-orange-600">On-Demand Access</th>
                                            <th className="px-6 py-4 text-center text-sm font-bold text-blue-600">White-Label Infrastructure</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-slate-200">
                                        {[{
                    feature: 'Platform commitment',
                    ondemand: '✗ None',
                    whitelabel: '✓ By team size'
                  }, {
                    feature: 'Platform interface for your team',
                    ondemand: '✗',
                    whitelabel: '✓ Full access'
                  }, {
                    feature: 'You act as CSM',
                    ondemand: '✗ CrossPath CSM manages',
                    whitelabel: '✓ You manage directly'
                  }, {
                    feature: 'Mission visibility control',
                    ondemand: '✓ Via CSM',
                    whitelabel: '✓ Full self-service'
                  }, {
                    feature: 'Recruiter invite & outreach',
                    ondemand: '✗',
                    whitelabel: '✓ Active invite capability'
                  }, {
                    feature: 'Recruiter compensation flow',
                    ondemand: 'Direct by agency',
                    whitelabel: 'CrossPath or direct (by member type)'
                  }, {
                    feature: 'Commercial terms',
                    ondemand: 'Confirmed per mandate',
                    whitelabel: 'Confirmed at onboarding'
                  }, {
                    feature: 'Your branding',
                    ondemand: '✓',
                    whitelabel: '✓ Full white-label'
                  }, {
                    feature: 'Best for',
                    ondemand: 'Occasional overflow mandates',
                    whitelabel: 'Agencies wanting full infrastructure'
                  }].map(row => <tr key={row.feature} className="hover:bg-slate-50">
                                                <td className="px-6 py-4 text-sm font-semibold text-slate-900">{row.feature}</td>
                                                <td className="px-6 py-4 text-sm text-slate-600 text-center">{row.ondemand}</td>
                                                <td className="px-6 py-4 text-sm text-slate-600 text-center">{row.whitelabel}</td>
                                            </tr>)}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                </section>

                <section className="py-16 px-6 lg:px-8 bg-orange-500">
                    <div className="max-w-7xl mx-auto">
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
                            {[{
              value: '10,000+',
              label: 'Vetted recruiters in the network'
            }, {
              value: '42+',
              label: 'Countries covered'
            }, {
              value: '12 days',
              label: 'Average time to first client interview'
            }, {
              value: '~70%',
              label: 'Average profiles accepted at first client selection step'
            }].map((stat, index) => <div key={stat.label} className="text-center">
                                    <div className="text-4xl font-bold text-white mb-2">{stat.value}</div>
                                    <div className="text-orange-100 text-sm">{stat.label}</div>
                                </div>)}
                        </div>
                    </div>
                </section>

                <section className="py-20 px-6 lg:px-8 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
                    <div className="max-w-4xl mx-auto text-center">
                        <div>
                            <h2 className="text-4xl lg:text-5xl font-bold text-white mb-6">
                                Stop turning down mandates you can't cover.
                            </h2>
                            <p className="text-xl text-slate-300 mb-8">
                                Whether you need one mission covered or a full platform for your team — let's find the right model together.
                            </p>

                            <button type="button" onClick={() => setCalendlyOpen(true)} className="bg-orange-500 hover:bg-orange-600 text-white px-10 py-4 rounded-lg font-semibold text-lg transition-all shadow-lg hover:shadow-xl inline-flex items-center justify-center gap-2">
                                Book a Consultancy Call
                                <ArrowRight size={20} />
                            </button>

                            <p className="text-slate-400 text-sm mt-4">
                                30 minutes. No commitment. We find the right fit together.
                            </p>
                        </div>
                    </div>
                </section>

                <footer className="bg-slate-900 py-12 px-6 lg:px-8 border-t border-slate-800">
                    <div className="max-w-7xl mx-auto text-center">
                        <p className="text-slate-400">
                            © 2026 CrossPath. All rights reserved.
                        </p>
                    </div>
                </footer>

                {calendlyOpen && <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
                        <div className="bg-white rounded-xl w-full max-w-2xl h-[875px] relative">
                            <button type="button" onClick={() => setCalendlyOpen(false)} className="absolute top-4 right-4 text-slate-500 hover:text-black text-xl z-10 w-8 h-8 flex items-center justify-center bg-white rounded-full shadow-lg">
                                <XIcon size={20} />
                            </button>
                            <iframe src="https://calendly.com/emmanuel_beauvais/crosspath" width="100%" height="100%" frameBorder="0" className="rounded-xl" />
                        </div>
                    </div>}
            </div>
        </Page>;
});
};
